import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { useCurrentUser } from "../lib/useCurrentUser";
import NotificationBell from "./NotificationBell";

const baseLink =
  "rounded-md px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white";
const activeLink = "bg-white/10 text-white";

export default function Navbar() {
  const { user } = useCurrentUser();
  const role = user?.role;
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-30 border-b border-white/5 bg-ink-950/85 backdrop-blur">
      <div className="container-page flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <ShieldMark className="h-7 w-7 shrink-0" />
          <div className="min-w-0 leading-tight">
            <div className="truncate text-sm font-semibold text-white">
              Community Shield OS
            </div>
            <div className="hidden text-[11px] uppercase tracking-wider text-slate-400 sm:block">
              Trust · Intelligence · Coordination
            </div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <SignedIn>
            <NavLink to="/dashboard" className={navClass}>
              Dashboard
            </NavLink>
            <NavLink to="/alerts" className={navClass}>
              Alerts
            </NavLink>
            <NavLink to="/submit-report" className={navClass}>
              Submit Report
            </NavLink>
            <NavLink to="/my-reports" className={navClass}>
              My Reports
            </NavLink>
            <NavLink to="/organizations" className={navClass}>
              Organizations
            </NavLink>
            <NavLink to="/resources" className={navClass}>
              Resources
            </NavLink>
            {role === "organization_admin" && (
              <NavLink to="/organization-reports" className={navClass}>
                My Org Reports
              </NavLink>
            )}
            {(role === "trusted_verifier" || role === "platform_admin") && (
              <NavLink to="/verifier" className={navClass}>
                Verifier
              </NavLink>
            )}
            {role === "platform_admin" && (
              <>
                <NavLink to="/admin/pilot" className={navClass}>
                  Pilot
                </NavLink>
                <NavLink to="/admin" className={navClass}>
                  Admin
                </NavLink>
                <NavLink to="/admin/users" className={navClass}>
                  Users
                </NavLink>
              </>
            )}
          </SignedIn>
          <SignedOut>
            <NavLink to="/privacy" className={navClass}>
              Privacy
            </NavLink>
            <NavLink to="/request-access" className={navClass}>
              Request access
            </NavLink>
          </SignedOut>
        </nav>

        <div className="flex items-center gap-2">
          <SignedOut>
            <Link
              to="/sign-in"
              className="hidden rounded-md border border-white/10 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-white/5 sm:inline-flex"
            >
              Sign in
            </Link>
          </SignedOut>
          <SignedIn>
            <NotificationBell />
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
          <button
            type="button"
            aria-label="Toggle navigation menu"
            aria-expanded={open}
            onClick={() => setOpen((s) => !s)}
            className="rounded-md border border-white/10 p-2 text-slate-200 hover:bg-white/5 lg:hidden"
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/5 bg-ink-950/95 lg:hidden">
          <nav className="container-page flex flex-col gap-1 py-3">
            <SignedIn>
              <MobileLink to="/dashboard">Dashboard</MobileLink>
              <MobileLink to="/alerts">Alerts</MobileLink>
              <MobileLink to="/submit-report">Submit report</MobileLink>
              <MobileLink to="/my-reports">My reports</MobileLink>
              <MobileLink to="/organizations">Organizations</MobileLink>
              <MobileLink to="/resources">Resources</MobileLink>
              {role === "organization_admin" && (
                <MobileLink to="/organization-reports">
                  My organization reports
                </MobileLink>
              )}
              {(role === "trusted_verifier" || role === "platform_admin") && (
                <MobileLink to="/verifier">Verifier queue</MobileLink>
              )}
              {role === "platform_admin" && (
                <>
                  <MobileLink to="/admin/pilot">Pilot console</MobileLink>
                  <MobileLink to="/admin">Admin review</MobileLink>
                  <MobileLink to="/admin/users">User management</MobileLink>
                </>
              )}
              <MobileLink to="/privacy">Privacy & trust</MobileLink>
            </SignedIn>
            <SignedOut>
              <MobileLink to="/privacy">Privacy & trust</MobileLink>
              <MobileLink to="/request-access">Request access</MobileLink>
              <MobileLink to="/sign-in">Sign in</MobileLink>
            </SignedOut>
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `block rounded-md px-3 py-2 text-sm font-medium ${isActive ? "bg-white/10 text-white" : "text-slate-200 hover:bg-white/5"}`
      }
    >
      {children}
    </NavLink>
  );
}

function navClass({ isActive }: { isActive: boolean }) {
  return `${baseLink} ${isActive ? activeLink : ""}`;
}

function ShieldMark({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <path
        d="M32 8l20 8v14c0 13-8.6 24.3-20 27-11.4-2.7-20-14-20-27V16l20-8z"
        fill="none"
        stroke="#f5b14a"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <path
        d="M22 33l7 7 13-14"
        fill="none"
        stroke="#f5b14a"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="6" y1="18" x2="18" y2="6" />
    </svg>
  );
}
